import { Config, SiteConfigItem } from '../models/config';
import { Observable, combineLatest, of } from 'rxjs';
import { RoleItem, Roles } from '../models/roles';

import { AppState } from '../store/states/app.state';
import { ConfigHelpers } from '../config/config.helpers';
import { Permission } from './permission';
import { Permissions } from '../models/permissions';
import { Store } from '@ngrx/store';
import { User } from '../models/user';
import { UserPermissions } from '../models/user-permissions';
import { map } from 'rxjs/operators';
import { selectUserPermissions } from '../store/selectors/user-permissions.selector';
import { selectUser } from '../store/selectors/user.selector';
import { AllowedPriviledge } from './auth';
import { BaseModel } from '../base/base-model';
import { routePermissions } from '@eview/core/domain/navigation/navigation.helpers';
import {
  FormGrant,
  FormGrantAction,
  GLOBAL_CAPABILITY_CODES,
  GlobalCapability,
  LEGACY_FORM_IDS,
  expandFormGrantAction
} from './form-grants';
const SUPER_ROLES = ['admin', 'sysadmin', 'super'];

const GetPermissionsVector = (permissions: Permissions): Permission[] =>
  permissions.results.map(p => p.name as Permission);

const GetRoleByName = (roles: Roles, name: string): RoleItem => {
  return roles.results.find(r => r.name === name) || null;
};

const GetRolePermissionsByName = (roles: Roles, name: string): Permission[] => {
  return (GetRoleByName(roles, name) || { permissions: [] }).permissions;
};

const GetRoleFormGrants = (roles: Roles, name: string): FormGrant[] => {
  return (GetRoleByName(roles, name) || { form_grants: [] }).form_grants || [];
};

const GetUserPermissions = (
  user: User,
  config: Config,
  permissions: Permissions,
  roles: Roles
): UserPermissions => {
  const { role: userRoleName } = user;
  const isAdmin = SUPER_ROLES.includes(userRoleName);

  // Prefer /users/me grants; if empty, enrich from /roles catalog (same role name).
  const fromMe = user.form_grants;
  const fromRoles = roles ? GetRoleFormGrants(roles, userRoleName) : [];
  const form_grants =
    fromMe && fromMe.length
      ? fromMe
      : fromRoles && fromRoles.length
        ? fromRoles
        : fromMe || fromRoles || [];

  let rolePermissions: Permission[] = [];
  if (user.permissions && user.permissions.length) {
    rolePermissions = user.permissions as Permission[];
  } else if (isAdmin && permissions) {
    rolePermissions = GetPermissionsVector(permissions);
  } else if (roles) {
    rolePermissions = GetRolePermissionsByName(roles, userRoleName);
  }

  const userPermissions: UserPermissions = {
    isAdmin,
    permissions: rolePermissions,
    form_grants
  };
  userPermissions.permissions = removeUserPermissionsDupes(
    userPermissions.permissions
  );
  return userPermissions;
};

/** Fast path after /users/me — no full roles/permissions catalog required. */
const GetUserPermissionsFromMe = (user: User): UserPermissions => {
  const isAdmin = SUPER_ROLES.includes(user?.role);
  const form_grants = Array.isArray(user?.form_grants) ? user.form_grants : [];
  return {
    isAdmin,
    permissions: removeUserPermissionsDupes(
      ((user?.permissions as Permission[]) || []).slice()
    ),
    form_grants
  };
};

const customPermissionsHelpers = {
  SubmitPosts: (
    user: User,
    config: Config,
    permissions: Permissions = null,
    roles: Roles = null
  ): Permission => {
    const permission = Permission.CreateReport;
    if (user) return permission;
    const siteConfig = ConfigHelpers.GetConfigItem<SiteConfigItem>(
      SiteConfigItem,
      config
    );
    if (siteConfig.private) return null;
    return permission;
  }
};

const removeUserPermissionsDupes = (
  permissions: Permission[]
): Permission[] => [...new Set(permissions)];

const HasUserPermission = (
  store: Store<AppState>,
  permission: Permission | Permission[]
): Observable<boolean> => {
  return permission
    ? store.select(selectUserPermissions).pipe(
        map(userPermissions => {
          if (!userPermissions) {
            return false;
          }
          if (userPermissions.isAdmin) {
            return true;
          }
          if (Array.isArray(permission)) {
            return permission.some(
              p =>
                userPermissions.permissions.includes(p) ||
                hasGlobalSync(userPermissions, p as string)
            );
          }
          return (
            userPermissions.permissions.includes(permission) ||
            hasGlobalSync(userPermissions, permission as string)
          );
        })
      )
    : of(true);
};

const hasGlobalSync = (
  userPermissions: UserPermissions,
  code: string
): boolean => {
  if (!userPermissions) {
    return false;
  }
  if (userPermissions.isAdmin) {
    return true;
  }
  if (userPermissions.permissions.includes(code as Permission)) {
    return true;
  }
  // Map legacy string ↔ new global codes
  const mapLegacy: Record<string, string[]> = {
    [Permission.UserManagement]: [GlobalCapability.UsersManage, Permission.UserManagement],
    [Permission.AccessManagement]: [GlobalCapability.RolesManage, Permission.AccessManagement],
    [Permission.AccessAndEditIncidentIndicators]: [
      GlobalCapability.TagsManage,
      Permission.AccessAndEditIncidentIndicators
    ],
    [Permission.AccessAndEditIncidentReportsFormNamesOfDeports]: [
      GlobalCapability.FormsConfigure,
      Permission.AccessAndEditIncidentReportsFormNamesOfDeports
    ],
    [Permission.AccessAndModifyParametersSettings]: [
      GlobalCapability.SettingsManage,
      Permission.AccessAndModifyParametersSettings
    ],
    [GlobalCapability.UsersManage]: [GlobalCapability.UsersManage, Permission.UserManagement],
    [GlobalCapability.RolesManage]: [GlobalCapability.RolesManage, Permission.AccessManagement],
    [GlobalCapability.TagsManage]: [
      GlobalCapability.TagsManage,
      Permission.AccessAndEditIncidentIndicators
    ],
    [GlobalCapability.FormsConfigure]: [
      GlobalCapability.FormsConfigure,
      Permission.AccessAndEditIncidentReportsFormNamesOfDeports
    ],
    [GlobalCapability.SettingsManage]: [
      GlobalCapability.SettingsManage,
      Permission.AccessAndModifyParametersSettings
    ]
  };
  const alts = mapLegacy[code] || [code];
  return alts.some(a => userPermissions.permissions.includes(a as Permission));
};

const canFormSync = (
  userPermissions: UserPermissions,
  formId: number | null,
  action: FormGrantAction
): boolean => {
  if (!userPermissions) {
    return false;
  }
  if (userPermissions.isAdmin) {
    return true;
  }
  const grants = userPermissions.form_grants || [];
  if (grants.length) {
    // formId === null means "any form" only for menu-level actions.
    // Mutating / post-scoped actions MUST pass a concrete form id (or all_forms grant).
    // Otherwise a grant on form 2 incorrectly unlocks form 1 posts.
    const short = String(action).replace(/_(own|any)$/, '');
    const allowsUnscopedAnyForm =
      short === 'view' ||
      short === 'create' ||
      short === 'analysis' ||
      short === 'export';

    const wanted = expandFormGrantAction(action);

    const grantHasAction = (g: FormGrant): boolean => {
      if (!g.actions?.length) {
        return false;
      }
      const acts = g.actions as string[];
      for (const w of wanted) {
        if (acts.includes(w)) {
          return true;
        }
      }
      // Legacy short names still in payload
      if (acts.includes(short) || acts.includes(String(action))) {
        return true;
      }
      if (
        (String(action).startsWith('publish') || short === 'publish') &&
        acts.includes('publish')
      ) {
        return true;
      }
      return false;
    };

    for (const g of grants) {
      if (!grantHasAction(g)) {
        continue;
      }
      if (g.all_forms) {
        return true;
      }
      if (formId != null && Number(g.form_id) === Number(formId)) {
        return true;
      }
      if (formId == null && allowsUnscopedAnyForm && g.form_id != null) {
        return true;
      }
    }
    // Role has grant rows but none match this action/form
    return false;
  }
  // Dual-read: only when the role has no grant rows yet
  return hasLegacyFormPermission(userPermissions, formId, action);
};

const hasLegacyFormPermission = (
  up: UserPermissions,
  formId: number | null,
  action: FormGrantAction
): boolean => {
  const ce = LEGACY_FORM_IDS.conflictividadElectoral;
  const oe = LEGACY_FORM_IDS.operacionesElectorales;

  // Map expanded codes to short keys used in legacy enum pairs
  const short = String(action)
    .replace(/_own$/, '')
    .replace(/_any$/, '') as FormGrantAction;

  const byAction: Partial<Record<string, [Permission, Permission]>> = {
    view: [
      Permission.ViewReportsListConflictividadElectoral,
      Permission.ViewReportsListElectionOperations
    ],
    create: [
      Permission.CreateANewreportConflictividadElectoral,
      Permission.CreateANewReportOperacionesElectorales
    ],
    edit: [
      Permission.EditOwnIncidentReportContentConflictividadElectoral,
      Permission.EditOwnIncidentReportContentOperacionesElectorales
    ],
    edit_own: [
      Permission.EditOwnIncidentReportContentConflictividadElectoral,
      Permission.EditOwnIncidentReportContentOperacionesElectorales
    ],
    edit_any: [
      Permission.EditAndManageReportsConflictividadElectoral,
      Permission.EditAndManageReportsOperacionesElectorales
    ],
    delete: [
      Permission.DeleteReportsConflictividadElectoral,
      Permission.DeleteReportsOperacionesElectorales
    ],
    comment: [
      Permission.CommentReportConflictividadElectoral,
      Permission.CommentReportOperacionesElectorales
    ],
    view_identity: [
      Permission.ViewIncidentReporterIdentityConflictividadElectoral,
      Permission.ViewIncidentReporterIdentityOperacionesElectorales
    ],
    export: [
      Permission.ExportIncidentListConflictividadElectoral,
      Permission.ExportIncidentListOperacionesElectorales
    ],
    analysis: [
      Permission.AccessAnalysisDashboardConflictividadElectoral,
      Permission.AccessAnalysisDashboardOperacionesElectorales
    ],
    publish: [
      Permission.EditAndManageReportsConflictividadElectoral,
      Permission.EditAndManageReportsOperacionesElectorales
    ],
    publish_any: [
      Permission.EditAndManageReportsConflictividadElectoral,
      Permission.EditAndManageReportsOperacionesElectorales
    ],
    publish_own: [
      Permission.EditOwnIncidentReportContentConflictividadElectoral,
      Permission.EditOwnIncidentReportContentOperacionesElectorales
    ]
  };
  const pair =
    byAction[String(action)] || byAction[short] || byAction[`${short}_any`];
  if (!pair) {
    return false;
  }
  const [pCe, pOe] = pair;
  if (formId === null) {
    return up.permissions.includes(pCe) || up.permissions.includes(pOe);
  }
  if (Number(formId) === ce) {
    return up.permissions.includes(pCe);
  }
  if (Number(formId) === oe) {
    return up.permissions.includes(pOe);
  }
  return false;
};

const accessibleFormIdsSync = (
  userPermissions: UserPermissions,
  action: FormGrantAction = 'view'
): number[] | null => {
  if (!userPermissions) {
    return [];
  }
  if (userPermissions.isAdmin) {
    return null;
  }
  const grants = userPermissions.form_grants || [];
  const wanted = expandFormGrantAction(action);
  const ids: number[] = [];
  for (const g of grants) {
    const acts = (g.actions || []) as string[];
    const has = wanted.some(w => acts.includes(w)) || acts.includes(String(action));
    if (!has) {
      continue;
    }
    if (g.all_forms) {
      return null;
    }
    if (g.form_id != null) {
      ids.push(Number(g.form_id));
    }
  }
  if (ids.length) {
    return [...new Set(ids)];
  }
  // legacy fallbacks
  const legacy: number[] = [];
  if (canFormSync(userPermissions, LEGACY_FORM_IDS.conflictividadElectoral, action)) {
    legacy.push(LEGACY_FORM_IDS.conflictividadElectoral);
  }
  if (canFormSync(userPermissions, LEGACY_FORM_IDS.operacionesElectorales, action)) {
    legacy.push(LEGACY_FORM_IDS.operacionesElectorales);
  }
  return legacy;
};

const CanForm = (
  store: Store<AppState>,
  formId: number | null,
  action: FormGrantAction
): Observable<boolean> =>
  store.select(selectUserPermissions).pipe(
    map(up => canFormSync(up, formId, action))
  );

const CanGlobal = (
  store: Store<AppState>,
  code: string
): Observable<boolean> =>
  store.select(selectUserPermissions).pipe(
    map(up => hasGlobalSync(up, code))
  );

const AccessibleFormIds = (
  store: Store<AppState>,
  action: FormGrantAction = 'view'
): Observable<number[] | null> =>
  store.select(selectUserPermissions).pipe(
    map(up => accessibleFormIdsSync(up, action))
  );

const HasRouteAccessPermission = (
  store: Store<AppState>,
  route: any
): Observable<boolean> => {
  const path = String(route || '')
    .split('?')[0]
    .split('#')[0]
    .replace(/\/+$/, '');
  const matchingRoutes = routePermissions.filter(
    o => path === o.path || path.startsWith(o.path + '/')
  );
  if (!matchingRoutes.length) {
    // Unlisted routes stay open (legacy pages without form grants).
    return of(true);
  }
  return combineLatest([
    store.select(selectUser),
    store.select(selectUserPermissions)
  ]).pipe(
    map(([user, userPermissions]) => {
      // Anonymous: only public view routes (map/list of published reports).
      // Do NOT apply form grants / roles to guests.
      if (!user) {
        return matchingRoutes.some(routeObj => routeObj.formAction === 'view');
      }
      if (!userPermissions) {
        return false;
      }
      if (userPermissions.isAdmin) {
        return true;
      }
      return matchingRoutes.some(routeObj => {
        if (routeObj.formAction) {
          return canFormSync(
            userPermissions,
            routeObj.formId ?? null,
            routeObj.formAction
          );
        }
        if (routeObj.global) {
          return hasGlobalSync(userPermissions, routeObj.global);
        }
        return (
          hasGlobalSync(userPermissions, routeObj.permission as string) ||
          userPermissions.permissions.includes(routeObj.permission)
        );
      });
    })
  );
};

const HasPriviledgeOn = (
  object: BaseModel,
  priviledge: AllowedPriviledge
): boolean =>
  object.allowed_privileges
    ? object.allowed_privileges.includes(priviledge)
    : false;

export const AuthHelpers = {
  Permissions: { GetPermissionsVector },
  Roles: { GetRoleByName, GetRolePermissionsByName, GetRoleFormGrants },
  User: {
    GetUserPermissions,
    GetUserPermissionsFromMe,
    HasUserPermission,
    HasRouteAccessPermission,
    CanForm,
    CanGlobal,
    AccessibleFormIds,
    canFormSync,
    hasGlobalSync,
    accessibleFormIdsSync
  },
  Resources: {
    HasPriviledgeOn
  },
  Catalog: {
    GLOBAL_CAPABILITY_CODES,
    SUPER_ROLES
  }
};
